import React, {useMemo, useState} from 'react';
import {IonAlert, IonIcon} from '@ionic/react';
import {statsChartSharp} from 'ionicons/icons';
import {CountyData} from "../../interfaces/CountyData";
import {useHistory} from 'react-router-dom';

import Panel from "../ui/Panel";
import RankedBars, {RankedBarRow} from "../ui/RankedBars";
import "./CardParts.css";

/** Each view ranks something different, so the subtitle names it. */
const SUBTITLE: Record<string, (miles: number) => string> = {
  miles: miles => `${Math.floor(miles)} congested miles`,
  pti: () => 'Planning time index, evening peak',
  corridors: () => 'Slower than the same period last year',
};

interface GraphDataCardprops {
  content: any;
  crashList: CountyData[];
  newdata: any;
}

const GraphDataCard: React.FC<GraphDataCardprops> = (props: GraphDataCardprops) => {
  const [modalOpen, setModalOpen] = useState(false);
  const history = useHistory();

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);
  const handleOkay = () => {
    closeModal();
    history.push('/AppCenter/Congestion');
  };

  const totalMiles = Number(props.newdata.congested_miles.value);
  // Three questions about the same network: where congestion covers the most
  // road, where trips are least dependable, and which corridors are worse
  // than they were a year ago. The first two rank counties, the third ranks
  // roads -- the toggle says which one is on screen.
  const [sortBy, setSortBy] = useState<'miles' | 'pti' | 'corridors'>('miles');

  /** The verdict text is written for an engineer; a row has room for a word. */
  const VERDICT_LABEL: Record<string, string> = {
    'work zones in both periods': 'work zone',
    'possible construction': 'construction?',
    'no work zones nearby': 'not a work zone',
  };

  const rows: RankedBarRow[] = useMemo(() => {
    if (sortBy === 'corridors') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const corridors = (props.newdata.worseningCorridors ?? []) as any[];
      const worst = Math.max(...corridors.map(c => c.secPerMi ?? 0), 1);
      return corridors.map(c => ({
        name: c.county ? `${c.road} \u00b7 ${c.county}` : c.road,
        display: `+${c.secPerMi}s/mi${VERDICT_LABEL[c.verdict] ? ` \u00b7 ${VERDICT_LABEL[c.verdict]}` : ''}`,
        percent: Math.round((c.secPerMi / worst) * 100),
      }));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const counties = [...(props.newdata.countyCongestion ?? [])] as any[];
    const worstPti = Math.max(...counties.map(c => c.pti ?? 0), 1.01);

    counties.sort((a, b) => sortBy === 'miles'
      ? b.miles - a.miles
      : (b.pti ?? 0) - (a.pti ?? 0));

    return counties.map(county => ({
      name: county.name,
      display: county.pti
        ? `${Math.floor(county.miles)} mi \u00b7 PTI ${county.pti}`
        : `${Math.floor(county.miles)} mi`,
      // Bars follow whichever measure is being ranked. PTI scales from 1.0,
      // since every county sits above it and a zero-based bar would flatten
      // the differences out of sight.
      percent: sortBy === 'miles'
        ? Math.floor((county.miles / totalMiles) * 100)
        : Math.round(((county.pti ?? 1) - 1) / (worstPti - 1) * 100),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.newdata.countyCongestion, props.newdata.worseningCorridors, sortBy, totalMiles]);

  return (
    <>
      <Panel
        title="Congestion"
        accent="var(--domain-congestion)"
        icon={<IonIcon icon={statsChartSharp} />}
        subtitle={SUBTITLE[sortBy](totalMiles)}
        onInfo={openModal}
        infoLabel="About congestion"
        footer={`Updated ${props.content.updated}`}
      >
        <dl className="stat-pair">
          <div className="stat-pair__item">
            <dt>PTI</dt>
            <dd>{props.newdata.pti.value}</dd>
          </div>
          <div className="stat-pair__item">
            <dt>TTI</dt>
            <dd>{props.newdata.tti.value}</dd>
          </div>
        </dl>

        <div className="sort-toggle" role="group" aria-label="Rank congestion by">
          <button
            type="button"
            className={sortBy === 'miles' ? 'sort-toggle__btn sort-toggle__btn--on' : 'sort-toggle__btn'}
            onClick={() => setSortBy('miles')}
          >
            Congested miles
          </button>
          <button
            type="button"
            className={sortBy === 'pti' ? 'sort-toggle__btn sort-toggle__btn--on' : 'sort-toggle__btn'}
            onClick={() => setSortBy('pti')}
          >
            Least reliable
          </button>
          <button
            type="button"
            className={sortBy === 'corridors' ? 'sort-toggle__btn sort-toggle__btn--on' : 'sort-toggle__btn'}
            onClick={() => setSortBy('corridors')}
          >
            Getting worse
          </button>
        </div>
        <div className="dashboard__scroll">
          <RankedBars rows={rows} accent="var(--domain-congestion)" />
        </div>
      </Panel>

      <IonAlert
        isOpen={modalOpen}
        header="Congestion Miles"
        subHeader={"Congestion Information"}
        message={"Click below for more information on congestion"}
        buttons={[{text: "More Information", handler: handleOkay}]}
        onDidDismiss={closeModal}
        cssClass="bigger-alert"
      ></IonAlert>
    </>
  );
};
export default GraphDataCard;
